// AdsPanel — right-side advertisement/resource panel (v2).
//
// Mirrors v1's resource carousel: fetches GET /resource/:hospitalId, sorts by
// seqnum, and rotates — images on a timer, videos when they end.
//
// Audio: the ad plays with sound from the start and is paused only while a
// queue announcement speaks (utils/adAudio). Each video still starts the way
// every TV browser renders reliably — the autoplay + muted attributes — and
// is unmuted once it is playing; a browser that refuses sound without a
// gesture keeps it running silently and sound is retried on the first click
// and after each announcement.
//
// Layout follows v1: a plain full-size box. The rounded, clipped, black
// container v2 used to have is exactly what TV browsers that draw video as a
// separate surface cannot clip — the result was the black background with
// the audio still playing.
import React, { useEffect, useRef, useState } from "react";
import { AdResource, getAdResources } from "../api/resources";
import { armUnmuteOnInteraction, unmuteAd } from "../utils/adAudio";
import {
  forgetLocal,
  isLocalUrl,
  playableUrl,
  prefetchAdVideos,
} from "../utils/adCache";

const IMAGE_MS = 8000;

interface Props {
  hospitalId?: string;
  // Rendered instead of nothing when the hospital has no ads configured (or
  // while the first fetch is in flight). The side panel leaves this unset and
  // collapses to nothing as before; the full-screen idle board passes the
  // idle panel so an ads-enabled TV never goes blank.
  fallback?: React.ReactNode;
  // How the ad fills its area. "cover" (default, the side panel next to the
  // queue) fills the panel and crops. "contain" (the full-screen idle board)
  // uses the full height and keeps the ad's own shape, centred: a reel or
  // short stands tall in the middle, a landscape video fits the height and
  // is never cropped or stretched across the whole screen.
  fit?: "cover" | "contain";
}

const AdsPanel: React.FC<Props> = ({
  hospitalId,
  fallback,
  fit = "cover",
}) => {
  const [resources, setResources] = useState<AdResource[]>([]);
  const [index, setIndex] = useState(0);
  const strikesRef = useRef(0);
  const [adsDead, setAdsDead] = useState(false);

  useEffect(() => {
    if (!hospitalId) return;
    let cancelled = false;
    (async () => {
      const list = await getAdResources(hospitalId);
      if (cancelled) return;
      setResources([...list].sort((a, b) => (a.seqnum ?? 0) - (b.seqnum ?? 0)));
      setIndex(0);
    })();
    return () => {
      cancelled = true;
    };
  }, [hospitalId]);

  const current = resources[index];
  const isVideo = !!current?.category?.toLowerCase().includes("video");
  const advance = () =>
    setIndex((i) => (resources.length ? (i + 1) % resources.length : 0));

  // Download every video once into the TV's own storage (see utils/adCache);
  // from the next rotation on they play locally. Images are small and the
  // normal HTTP cache handles them.
  useEffect(() => {
    prefetchAdVideos(
      resources
        .filter((r) => r.category?.toLowerCase().includes("video"))
        .map((r) => r.resource_url)
    );
  }, [resources]);

  // Where this video plays from: the local copy when one exists, else the CDN.
  // Bumped by `retry` to fall back to the network if the local copy fails.
  const [retry, setRetry] = useState(0);
  const videoSrc = isVideo && current ? playableUrl(current.resource_url) : "";

  // Sound on once the video is actually playing (unless an announcement has
  // the ads ducked — unmuteAd checks). Keyed on the resource so it runs once
  // per mounted <video>.
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !isVideo) return;
    let cancelled = false;
    const onPlaying = () => {
      if (!cancelled) unmuteAd(v);
    };
    if (!v.paused) onPlaying();
    else v.addEventListener("playing", onPlaying, { once: true });
    return () => {
      cancelled = true;
      v.removeEventListener("playing", onPlaying);
    };
  }, [current?.resource_id, isVideo, retry]);

  // A browser that blocked sound at start gets another go on the first click.
  useEffect(() => {
    armUnmuteOnInteraction();
  }, []);

  // Images rotate on a timer; videos advance when they finish.
  useEffect(() => {
    if (resources.length <= 1 || isVideo) return;
    const t = setTimeout(advance, IMAGE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, resources.length, isVideo]);

  // Advancement only happens on onEnded / the image timer, so a resource that
  // fails to load would otherwise park the panel on itself. Step past it, and
  // once every resource has failed hand back to the caller's fallback.
  const skip = (reason: string) => {
    console.warn("[queue-v2] skipping ad resource:", reason, current?.resource_url);
    strikesRef.current += 1;
    if (strikesRef.current >= resources.length) {
      setAdsDead(true);
      return;
    }
    advance();
  };

  if (resources.length === 0 || !current || adsDead) {
    return <>{fallback ?? null}</>;
  }

  const fitClass = fit === "contain" ? "object-contain" : "object-cover";
  const media = isVideo ? (
        <video
          key={`${current.resource_id}:${retry}`}
          ref={videoRef}
          src={videoSrc}
          autoPlay
          muted
          playsInline
          loop={resources.length === 1}
          onPlaying={() => {
            strikesRef.current = 0;
          }}
          onEnded={resources.length > 1 ? advance : undefined}
          onError={() => {
            // A broken local copy must not cost the ad: drop it and remount
            // the same video straight from the network.
            if (isLocalUrl(videoSrc)) {
              forgetLocal(current.resource_url);
              setRetry((n) => n + 1);
              return;
            }
            skip("video failed to load");
          }}
          className={`h-full w-full ${fitClass}`}
        />
      ) : (
        <img
          key={current.resource_id}
          src={current.resource_url}
          alt=""
          className={`h-full w-full ${fitClass}`}
          onLoad={() => {
            strikesRef.current = 0;
          }}
          onError={() => skip("image failed to load")}
        />
      );

  // The element always spans the area; with "contain" the picture sits
  // centred inside it and the page background shows at the sides (or top
  // and bottom), since nothing paints the letterbox.
  return (
    <div className="flex h-full w-full items-center justify-center">
      {media}
    </div>
  );
};

export default AdsPanel;
